import type {
  SchemaResponse,
  TableInfo,
  PagedRowsResponse,
  RowCreatedResponse,
  RowUpdatedResponse,
  RowDeletedResponse,
  ProblemDetails,
  FkEntryRequest,
  DistinctValuesResponse,
  MigrationsResponse,
  MigrationHistoryResponse,
  MigrationRunResponse,
  PresignedUploadResponse,
  FileCreatedResponse,
  PresignedDownloadResponse,
  FileListResponse,
  FileDeletedResponse,
  PagedVersionsResponse,
  PagedPendingVersionsResponse,
  PendingVersionsCountResponse,
  SampleVersionResponse,
  ReviewVersionRequest,
  ReviewVersionResponse,
  FieldCategoriesResponse,
  BatchPendingCheckResponse,
  ReviewProposedRequest,
  ReviewProposedResponse,
} from './types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

export class ApiError extends Error {
  constructor(
    public status: number,
    public problemDetails: ProblemDetails
  ) {
    super(problemDetails.detail || problemDetails.title)
    this.name = 'ApiError'
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string,
  confirmed = false
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (confirmed) {
    headers['X-Confirmed-Action'] = 'true'
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    let problemDetails: ProblemDetails
    try {
      problemDetails = await response.json()
    } catch {
      problemDetails = {
        type: 'about:blank',
        title: 'Error',
        status: response.status,
        detail: response.statusText,
      }
    }
    throw new ApiError(response.status, problemDetails)
  }

  return response.json()
}

export interface ApiClientOptions {
  /**
   * Function that returns the current auth token.
   * Using a getter ensures we always use the latest token after refresh.
   */
  getToken: () => string | null
  /**
   * Function to refresh the token. Called on 401 responses.
   * Should return the new token or null if refresh fails.
   */
  refreshToken: () => Promise<string | null>
}

export function createApiClient(options: ApiClientOptions) {
  const { getToken, refreshToken } = options

  const makeRequest = async <T>(
    endpoint: string,
    fetchOptions: RequestInit = {},
    confirmed = false,
    isRetry = false
  ): Promise<T> => {
    const token = getToken()
    try {
      return await request<T>(endpoint, fetchOptions, token ?? undefined, confirmed)
    } catch (error) {
      // On 401, try refreshing the token and retry once
      if (error instanceof ApiError && error.status === 401 && !isRetry) {
        const newToken = await refreshToken()
        if (newToken) {
          // Retry with new token
          return request<T>(endpoint, fetchOptions, newToken, confirmed)
        }
      }
      throw error
    }
  }

  return {
    // Schema endpoints
    async getSchema(): Promise<SchemaResponse> {
      return makeRequest('/api/schema')
    },

    async getTableInfo(tableName: string): Promise<TableInfo> {
      return makeRequest(`/api/schema/${encodeURIComponent(tableName)}`)
    },

    // Data endpoints
    async getRows(
      tableName: string,
      page = 1,
      pageSize = 50,
      options?: {
        sortColumn?: string
        sortDirection?: 'asc' | 'desc'
        filterColumn?: string
        filterValue?: string
      }
    ): Promise<PagedRowsResponse> {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      })
      if (options?.sortColumn) {
        params.set('sortColumn', options.sortColumn)
        params.set('sortDirection', options.sortDirection || 'asc')
      }
      if (options?.filterColumn && options?.filterValue) {
        params.set('filterColumn', options.filterColumn)
        params.set('filterValue', options.filterValue)
      }
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows?${params.toString()}`
      )
    },

    async getRow(
      tableName: string,
      pk: string
    ): Promise<Record<string, unknown>> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(pk)}`
      )
    },

    async createRow(
      tableName: string,
      data: Record<string, unknown>
    ): Promise<RowCreatedResponse> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows`,
        {
          method: 'POST',
          body: JSON.stringify({ data }),
        },
        true // confirmed
      )
    },

    async updateRow(
      tableName: string,
      pk: string,
      data: Record<string, unknown>
    ): Promise<RowUpdatedResponse> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(pk)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ data }),
        },
        true // confirmed
      )
    },

    async deleteRow(
      tableName: string,
      pk: string
    ): Promise<RowDeletedResponse> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(pk)}`,
        {
          method: 'DELETE',
        },
        true // confirmed
      )
    },

    /**
     * Creates a row with optional FK entries in a single transaction.
     * If any operation fails, all changes are rolled back.
     */
    async createRowWithFk(
      tableName: string,
      data: Record<string, unknown>,
      fkEntries?: FkEntryRequest[]
    ): Promise<RowCreatedResponse> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows/transactional`,
        {
          method: 'POST',
          body: JSON.stringify({ data, fkEntries }),
        },
        true // confirmed
      )
    },

    /**
     * Updates a row with optional FK entries in a single transaction.
     * If any operation fails, all changes are rolled back.
     */
    async updateRowWithFk(
      tableName: string,
      pk: string,
      data: Record<string, unknown>,
      fkEntries?: FkEntryRequest[]
    ): Promise<RowUpdatedResponse> {
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(pk)}/transactional`,
        {
          method: 'PUT',
          body: JSON.stringify({ data, fkEntries }),
        },
        true // confirmed
      )
    },

    /**
     * Gets distinct values for a column. Useful for filter dropdowns.
     */
    async getDistinctColumnValues(
      tableName: string,
      columnName: string,
      limit = 1000
    ): Promise<DistinctValuesResponse> {
      const params = new URLSearchParams({ limit: String(limit) })
      return makeRequest(
        `/api/tables/${encodeURIComponent(tableName)}/columns/${encodeURIComponent(columnName)}/values?${params.toString()}`
      )
    },

    // Migration endpoints (super user only)
    /**
     * Gets available migrations.
     */
    async getMigrations(): Promise<MigrationsResponse> {
      return makeRequest('/api/migrations')
    },

    /**
     * Gets migration run history.
     */
    async getMigrationHistory(): Promise<MigrationHistoryResponse> {
      return makeRequest('/api/migrations/history')
    },

    /**
     * Runs a migration by ID.
     */
    async runMigration(migrationId: string): Promise<MigrationRunResponse> {
      return makeRequest(
        `/api/migrations/${encodeURIComponent(migrationId)}/run`,
        { method: 'POST' },
        true // confirmed
      )
    },

    // File upload endpoints
    /**
     * Requests a presigned URL for uploading a file to S3.
     */
    async requestUploadUrl(
      filename: string,
      contentType: string,
      sizeBytes: number,
      sampleId: string
    ): Promise<PresignedUploadResponse> {
      return makeRequest(
        '/api/files/upload-url',
        {
          method: 'POST',
          body: JSON.stringify({ filename, contentType, sizeBytes, sampleId }),
        },
        true // confirmed
      )
    },

    /**
     * Confirms a file upload and creates a database record.
     * Call this after uploading the file to S3.
     */
    async confirmUpload(
      uploadId: string,
      filename: string,
      contentType: string,
      sizeBytes: number,
      sha256: string,
      sampleId: string
    ): Promise<FileCreatedResponse> {
      return makeRequest(
        '/api/files',
        {
          method: 'POST',
          body: JSON.stringify({
            uploadId,
            filename,
            contentType,
            sizeBytes,
            sha256,
            sampleId,
          }),
        },
        true // confirmed
      )
    },

    /**
     * Gets a presigned download URL for a file.
     */
    async getFileDownloadUrl(fileId: string): Promise<PresignedDownloadResponse> {
      return makeRequest(`/api/files/${encodeURIComponent(fileId)}/download-url`)
    },

    /**
     * Gets all files associated with a sample.
     */
    async getFilesForSample(sampleId: string): Promise<FileListResponse> {
      return makeRequest(`/api/files/sample/${encodeURIComponent(sampleId)}`)
    },

    /**
     * Deletes a file.
     */
    async deleteFile(fileId: string): Promise<FileDeletedResponse> {
      return makeRequest(
        `/api/files/${encodeURIComponent(fileId)}`,
        { method: 'DELETE' },
        true // confirmed
      )
    },

    // Version history endpoints
    /**
     * Gets version history for a sample.
     */
    async getSampleVersions(
      sampleId: string,
      page = 1,
      pageSize = 50
    ): Promise<PagedVersionsResponse> {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      })
      return makeRequest(
        `/api/versions/samples/${encodeURIComponent(sampleId)}?${params.toString()}`
      )
    },

    /**
     * Gets a single version by ID.
     */
    async getVersion(versionId: string): Promise<SampleVersionResponse> {
      return makeRequest(`/api/versions/${encodeURIComponent(versionId)}`)
    },

    /**
     * Gets pending versions awaiting admin review.
     */
    async getPendingVersions(
      page = 1,
      pageSize = 50,
      sampleId?: string
    ): Promise<PagedPendingVersionsResponse> {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      })
      if (sampleId) {
        params.set('sampleId', sampleId)
      }
      return makeRequest(`/api/versions/pending?${params.toString()}`)
    },

    /**
     * Gets count of pending versions for navigation badge.
     */
    async getPendingVersionsCount(): Promise<PendingVersionsCountResponse> {
      return makeRequest('/api/versions/pending/count')
    },

    /**
     * Reviews a pending version (accept, reject, or merge).
     * Requires db_admin role.
     */
    async reviewVersion(
      versionId: string,
      request: ReviewVersionRequest
    ): Promise<ReviewVersionResponse> {
      return makeRequest(
        `/api/versions/${encodeURIComponent(versionId)}/review`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        true // confirmed
      )
    },

    /**
     * Gets field categories for versioning behavior.
     */
    async getFieldCategories(): Promise<FieldCategoriesResponse> {
      return makeRequest('/api/versions/field-categories')
    },

    /**
     * Batch check which samples have pending changes.
     * Used by timeline and table views to show pending indicators.
     */
    async checkPendingSamples(sampleIds: string[]): Promise<BatchPendingCheckResponse> {
      if (sampleIds.length === 0) {
        return { pendingBySampleId: {} }
      }
      return makeRequest('/api/versions/pending/batch', {
        method: 'POST',
        body: JSON.stringify({ sampleIds }),
      })
    },

    /**
     * Reviews vendor proposed changes directly from samples.vendor_proposed.
     * Simpler interface for inline review UX.
     * Requires db_admin role.
     */
    async reviewProposed(
      sampleId: string,
      request: ReviewProposedRequest
    ): Promise<ReviewProposedResponse> {
      return makeRequest(
        `/api/versions/samples/${encodeURIComponent(sampleId)}/review-proposed`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        },
        true // confirmed
      )
    },
  }
}

export type ApiClient = ReturnType<typeof createApiClient>
